import { StapSoort } from "@/containers/library/vraagDisplay/NewStappenplan/types";
import { UserRole } from "./parent_student_types";
import { Profiel } from "./profiel_types";
import { SubvraagInfo } from "@/services/api_types";
import { Platform } from "@/hooks/useLayoutMode";
import { TheorieGroep } from "@/services/api_types";

export interface SavedHuiswerkSession {
  hoofdstukId: number;
  vraagIds: number[];
  subvraagIds: number[];
  groepen: TheorieGroep[];
  completedSubvraagIds: number[];
  completedTheorieGroepIds: number[];
  completedSamenvattingGroepIds: number[];
  skippedSubvraagIds: number[];
}

export type FeatureDiscoveryKey =
  | "hint"
  | "stappenplan"
  | "bijlesbot"
  | "theorie"
  | "begripvraag"
  | "schrift-feedback"
  | "oefentoets";

export interface FeatureDiscoveryData {
  discovered_at: string;
  source: string;
  metadata: Record<string, unknown>;
}

export interface AiInteraction {
  question: string;
  answer: string;
}

export enum SubscriptionStatus {
  pending = "pending",
  active = "active",
  paused = "paused",
  past_due = "past_due",
  canceled = "canceled"
}

export interface UserData {
  created_at: string;
  first_name: string;
  last_name: string;
  email: string;
  full_name: string;
  role: UserRole;
  picture?: string;
}

export interface UserSubscriptionLinkDetails {
  linked_email?: string;
  linked_full_name?: string;
}

export interface UserDataResultSubscription {
  id: string;
  status: SubscriptionStatus;
  plan_id?: string;
  is_gift?: boolean;
  link_details?: UserSubscriptionLinkDetails;
  next_payment_date?: string;
  last_payment?: string;
  valid_until?: string;
  chargeback_status?: ChargebackStatus;
  paused_until?: string;
  billing_mode?: "recurring" | "legacy_lifetime" | "one_time";
  paid_in_full?: boolean;
  can_cancel?: boolean;
  can_pause?: boolean;
  can_resume?: boolean;
  can_recover_payment?: boolean;
  can_reauthorize?: boolean;
}

export interface ChargebackStatus {
  payment_created_at: string;
  charged_back_at: string;
  code: string;
}

export interface UserPreferences {
  opgave_in_boek_notice_v1_dismissed?: boolean;
}

export interface GetUserDataResult extends UserData {
  unlimited_access: boolean;
  is_premium: boolean;
  tour_status: "not_started" | "completed" | "skipped";

  subscriptions: UserDataResultSubscription[];

  free_trial: boolean;
  trial_end?: string;
  trial_expired?: boolean;

  organization?: string;
  organization_access_active?: boolean;

  pending_subscription?: boolean;

  profiel?: {
    jaar: number;
    niveau: string;
    wiskunde_type?: string;
    methode: string;
    editie: number;
    hoofdstuk_id?: number;
  };
  huiswerk_set?: SavedHuiswerkSession;
  user_preferences?: UserPreferences;

  feature_discoveries?: Partial<
    Record<FeatureDiscoveryKey, FeatureDiscoveryData>
  >;
}

export interface Vraag {
  id: number;
  titel: string;
  methode?: string | null;
  code: string;
  volgorde_nummer: number;
  subvragen?: SubvraagInfo[];
}

// TODO: TEMPORARY — remove ComingSoonVraag, VraagOrComingSoon, isComingSoonVraag when all vragen gaps are filled
export interface ComingSoonVraag {
  comingSoon: true;
  code: string;
}

export type VraagOrComingSoon = Vraag | ComingSoonVraag;

export function isComingSoonVraag(
  item: VraagOrComingSoon
): item is ComingSoonVraag {
  return "comingSoon" in item && item.comingSoon === true;
}

export interface Sectie {
  id: number;
  titel: string;
  is_oefentoets: boolean;
  is_html?: boolean;
  vragen: Vraag[];
}

export interface Hoofdstuk {
  id: number;
  titel: string;
  boek: number; // Just a number indicating the "deel"
  secties: Sectie[];
}

export interface ChatPhotoAttachment {
  imageId?: number;
  position: number;
  dataUri: string;
  status: "pending" | "ocr_done" | "ocr_failed";
  ocrLines?: string[];
  error?: string;
  dismissedAt?: string | null;
  // Stap waar de foto aan vasthangt. Gezet op capture-time en daarna
  // immutable; gebruikt om "foto's van vorige stap" te groeperen in de chat.
  stepIndex?: number | null;
  // Subvraag where this photo was captured. Frozen at capture-time — needed
  // to distinguish photos of the current exercise from leftover photos
  // captured while the session was still tied to a different subvraag.
  capturedSubvraagId?: number | null;
  // BoekTheorie equivalent — frozen at capture-time when the session was on
  // a theory scope. Oefentoets subvragen may carry both capturedVraagId and
  // capturedSubvraagId.
  capturedBoekTheorieId?: number | null;
  // Oefentoets equivalent — frozen at capture-time when the session was on
  // an oefentoets vraag scope.
  capturedVraagId?: number | null;
}

export interface ChatbotInteraction {
  id?: number;
  question: string;
  answer?: string;
  photos?: ChatPhotoAttachment[];
  fotoSessionId?: string;
}

export interface AskTutorRequestStap {
  type: StapSoort;
  stapIndex?: number;
}

export interface AskTutorRequest {
  conversation_id: string;
  sequence: number;
  prompt: string;
  interaction_history: ChatbotInteraction[];
  subvraag_id: number;
  stap: AskTutorRequestStap;
  user_is_mobile: boolean;
  is_zelf_uitwerken?: boolean;
  foto_session_id?: string;
  photos_attached_this_turn?: boolean;
  // Count of LaTeX chips from the drawing canvas present in the prompt
  // at submission time. Used client-side for analytics only — backend
  // sees the LaTeX inline in `prompt` and has no need for it.
  drawing_chip_count?: number;
  // Demo mode: sent when subvraag_id < 0
  demo_vraag_tekst?: string;
  demo_stappen_tekst?: string;
  demo_subvraag_letter?: string;
}

export type AcquisitionSource =
  | "tiktok"
  | "instagram"
  | "linkedin"
  | "newspaper"
  | "school"
  | "personal"
  | "hihep"
  | "thgh"
  | "snapchat"
  | "fd";

export interface PersonalizationFormRequest {
  school?: string;
  referrals?: AcquisitionSource[];
  referrals_custom?: string[];
  referrer_tiktok?: string;
  referrer_instagram?: string;
}

export interface CancellationFormRequest {
  form_type: "student" | "parent";
  main_reason?: string;
  detailed_reason?: string;
  open_feedback?: string;
}

export interface StappenplanFormRequest {
  subvraag_id: number | null;
  reden: string;
  free_text: string;
}

/* wiswiz #167: antwoord van GET /api/payment/outcome (anoniem) */
export type PaymentOutcome = "paid" | "open" | "canceled" | "expired" | "failed" | "charged_back";

export interface CheckPaymentResult {
  id: string;
  status: string;

  payment_type:
    | "direct_subscription"
    | "student_to_parent"
    | "parent_to_student";
  is_proefweek_payment: boolean;
  invite_token?: string;
  inviter_email?: string;
  inviter_full_name?: string;
  platform: Platform;
  // Set by the Mollie webhook when the prior-chargeback IBAN block fires.
  // Frontend uses this to render the dedicated blocked screen instead of the
  // personalisation form. Absent until the webhook commits a subscription.
  auto_canceled_prior_chargeback?: boolean;
}

export enum LoginErrors {
  user_not_found = "user_not_found",
  account_deactivated = "account_deactivated",
  account_blocked_prior_chargeback = "account_blocked_prior_chargeback",
  oauth_failed = "oauth_failed",
  unknown_error = "unknown_error"
}

export interface TheoryAskRequest {
  conversation_id: string;
  sequence: number;
  prompt: string;
  interaction_history: ChatbotInteraction[];
  boek_theorie_id: number;
  foto_session_id?: string;
  photos_attached_this_turn?: boolean;
}

export type WisWizModus = "huiswerk" | "uitleg";

export interface BegripVraag {
  stap: number;
  vraag: string;
  antwoord: string;
}

export enum SchoolRegio {
  noord = "noord",
  midden = "midden",
  zuid = "zuid",
  onbekend = "onbekend"
}

export type PauzeOptie = {
  regio: SchoolRegio;
  label: string;
  einddatum: string; // ISO date string vanuit FastAPI
};

export type PauzeOptiesResponse = Record<SchoolRegio, PauzeOptie>;

export interface SubscriptionPauseResponse {
  paused_until: string;
  region: SchoolRegio;
}
